import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {consoleLogger, CoreDI, DataStorage, ICommandLineInterface, IInputGestures, inject} from '@gongxigames/framework/core';
import {AbstractFilter, ArrayFilter, LogicEvents} from '@gongxigames/framework/slot/services/logic';
import {GlobalEventsMap} from '@gongxigames/framework/slot/events';
import {
    CheatAction,
    CheatPlugin,
    CheatPlugins,
    CheatPluginsObjects,
    DataProps,
    ICheatsModule,
    ICheatsModuleContext
} from './CheatModuleInterfaces';
import {setVisibilityStats, setVisibleStats} from './setVisibleStats';
import {setDisplayRewind, setVisibleRewind} from './setVisibleRewind';
import {CheatsView} from './CheatsView';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {KeyboardKeyCodes} from '@gongxigames/framework/core/types/IKeyboard';

export class CheatsModule extends Module<CheatsView> implements ICheatsModule {

    public static viewID: string = CheatsView.NAME;

    public static readonly NAME: symbol = Symbol('CheatsModule');

    protected static plugins: CheatPlugins = {};

    protected _arrayFilter: ArrayFilter = new ArrayFilter();
    protected _storage!: DataStorage<DataProps>;
    protected _plugins: CheatPluginsObjects = {};
    protected _context!: ICheatsModuleContext;

    @inject(CoreDI.InputGestures)
    protected _InputGestures!: IInputGestures;

    @inject(CoreDI.CLI)
    protected _cli!: ICommandLineInterface;

    public static registerPlugin(plugin: CheatPlugin): void {
        this.plugins[plugin.name] = plugin;
    }

    protected createContext(): void {
        this._context = {
            storage: this._storage,
            removeCheatAction: this.removeCheatAction.bind(this)
        };
    }

    public init(): void {
        this._states = {};

        this.createStorage();
        this.createContext();
        this.createPlugins();
        this.setupViews();
        this.setupStates();
        this.setupEvents();
        this.onInitialized();
        this.addConsoleFunctionality();
    }

    protected addConsoleFunctionality(): void {
        this.disableContextMenu();
    }

    protected createPlugins(): void {
        Object.keys(CheatsModule.plugins).forEach(name => {
            const plugin = CheatsModule.plugins[name];
            const [controllerConstructor, view] = plugin.init();
            const controller = new controllerConstructor();

            controller.init(this);

            this._plugins[name] = {name, controller, view};
        });
    }

    protected createStorage(): void {
        const defaultProps: Partial<DataProps> = {
            statsEnabled: true,
            rewindVisible: this.settings.get('rewindVisible'),
            cheatsInfoEnabled: true,
            cheatActions: [],
            visibleView: false,
            replayLastSpinEnabled: false
        };

        this._storage = new DataStorage(defaultProps);
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.eventManager.on('General.UI.KEY_DOWN', this.toggleChitMenu.bind(this), this);

        this._InputGestures.on('swipeToLeft', () => this.setVisibleView(false), this);
        this._InputGestures.on('swipeToRight', () => this.setVisibleView(true), this);

        this.storage.on('statsEnabled', setVisibleStats, this);
        this.storage.on('replayLastSpinEnabled', this.onSetLastReelSpin.bind(this), this);
        this.storage.on('rewindVisible', setVisibleRewind, this);
        this.storage.on('visibleView', this.onChangeVisibleView.bind(this), this);

        this.on(LogicEvents.RESPONSE, this.onResponseReceived);
        this.on(FlowModuleEvents.FEATURE_STARTED, this.onFeatureStarted);
        this.on('General.Flow.PAID_ROUND_ENDED', this.onPaidRoundEnded);
    }

    protected toggleChitMenu(keyCode: number) {
        if (keyCode === KeyboardKeyCodes.C) {
            this.setVisibleView(!this.storage.get('visibleView'));
        }
    }

    protected onResponseReceived(): void {
        this.logic.spinData && consoleLogger.log(this.logic.spinData);
        this.checkFreeSpin();

        consoleLogger.log(this.logic.getFinalSpinStopPositions().join(','));
    }

    protected onFeatureStarted(): void {
        this.storage.set('replayLastSpinEnabled', false);
        this.view.setState({replayLastSpinButtonDisabled: true});
    }

    protected onPaidRoundEnded(): void {
        this.view.setState({replayLastSpinButtonDisabled: false});
        this.storage.set('replayLastSpinEnabled', false);
    }

    protected checkFreeSpin(): void {
        const isFreeSpinActive = this.logic.previousAction === Action.FREESPIN && this.logic.nextAction === Action.FREESPIN;

        if (isFreeSpinActive) {
            this.view.setState({replayLastSpinButtonDisabled: false});
        }
    }

    public addCheatAction(action: CheatAction): void {
        let {cheatActions} = this.storage.data;
        const prevAction = cheatActions.find(act => act.id === action.id);

        if (prevAction) {
            cheatActions = this.removeCheatActionFromArray(prevAction.id, cheatActions);
        }

        // Applying an action asynchronously
        // necessary in order not to trigger a large chain of events
        Promise.resolve().then(() => action.apply());

        cheatActions.push(action);

        this._context.storage.set('cheatActions', cheatActions);
    }

    public removeCheatAction(action: CheatAction): void {
        const { cheatActions } = this._context.storage.data;
        const newCheatActions = this.removeCheatActionFromArray(action.id, cheatActions);

        if (newCheatActions.length !== cheatActions.length) {
            this._context.storage.set('cheatActions', newCheatActions);
        }
    }

    protected removeCheatActionFromArray(actionID: string, actions: CheatAction[]): CheatAction[] {
        const action = actions.find(action => action.id === actionID);

        if (action) {
            // Applying an action asynchronously
            // necessary in order not to trigger a large chain of events
            Promise.resolve().then(() => action.reject());
        }

        return actions.filter(action => action.id !== actionID);
    }

    protected setupStates(): void {}

    protected setupViews(): void {
        this.setView(CheatsModule.viewID);
    }

    protected setVisibleView(visible: boolean): void {
        this.storage.set('visibleView', visible);
    }

    protected onChangeVisibleView(visible: boolean): void {
        if (visible) {
            this.getEventManager<GlobalEventsMap>().emit('General.Promotion.HideCounter');
        } else {
            this.getEventManager<GlobalEventsMap>().emit('General.Promotion.ShowCounter');
        }

        setDisplayRewind(!visible);
        setVisibilityStats(!visible);
    }

    protected onSetLastReelSpin(enabled: boolean, oldValue: boolean): void {
        let stopPositions: number[] | null = null;

        if (enabled === oldValue) {
            return;
        }

        if (enabled) {
            stopPositions =
                this.getRespinInitialReelPositions() ||
                this.getFreeSpinInitialReelPositions() ||
                this.getMainSpinReelPositions() ||
                null;
        }

        this.logic.setFilterForStopPositions(stopPositions);
    }

    protected getFreeSpinInitialReelPositions(): number[] | undefined {
        const useFreeSpinRoundReelPositions = this.logic.previousAction === Action.FREESPIN && this.logic.nextAction === Action.SPIN;

        if (useFreeSpinRoundReelPositions) {
            return this.logic.getSessionForFreeSpinsWinningRound().reelPositions;
        }
    }

    protected getRespinInitialReelPositions(): number[] | undefined {
        const useRespinRoundReelPositions = this.logic.previousAction === Action.RESPIN && this.logic.nextAction !== Action.RESPIN;

        if (useRespinRoundReelPositions) {
            return this.logic.getSessionForRespinWinningRound().reelPositions;
        }
    }

    protected getMainSpinReelPositions(): number[] | undefined {
        try {
            return this.logic.getFinalSpinStopPositions();
        } catch (e) {
            consoleLogger.warn('Can not set last reel spin for the first spin');
            this.storage.set('replayLastSpinEnabled', false);
        }
    }

    public addArrayFilter(filter: AbstractFilter): void {
        this._arrayFilter.addFilter(filter);
        this.setArrayFilter();
    }

    public removeArrayFilter(filter: AbstractFilter): void {
        this._arrayFilter.removeFilterByName(filter.name);
        this.setArrayFilter();
    }

    public setArrayFilter(): void {
        this.logic.setFilter((!this._arrayFilter.isEmpty() ? this._arrayFilter : null) as any);
    }

    public get storage(): DataStorage<DataProps> {
        return this._storage;
    }

    public get context(): ICheatsModuleContext {
        return this._context;
    }

    private onInitialized(): void {
        if (this._view) {
            this.view.emit('moduleInitialized', {moduleContext: this._context, plugins: this._plugins});
        }
    }

    /**
     * Disabled the right click (or something else in mobile) context menu from appearing
     */
    private disableContextMenu() {
        document.querySelector<HTMLElement>('body > canvas')!.addEventListener('contextmenu', (e) => {
            this.storage.get('visibleView') && e.preventDefault();
        });
    }
}
